import type { ComponentPropsWithRef, ReactNode } from 'react'
import { cn } from '../../lib/cn'
import s from './Card.module.css'

/** Carte — la surface posée du prototype : fond carte, filet, coins, ombre légère. */
export type CardProps = ComponentPropsWithRef<'div'>

export function Card({ className, ...props }: CardProps) {
  return <div className={cn(s.root, className)} {...props} />
}

export interface CardHeaderProps {
  /** Icône de tête, en slot — l'app choisit son jeu d'icônes et sa taille. */
  icon?: ReactNode
  title: ReactNode
  /**
   * Le filet sous l'en-tête. `false` quand la carte porte déjà son rembourrage et que
   * l'en-tête ne fait que coiffer le contenu — la seconde forme du prototype.
   */
  divider?: boolean
  /** Ce qui s'aligne à droite : compteur, pastille d'état, action de la carte. */
  children?: ReactNode
  className?: string
}

/**
 * En-tête de carte — le `.card-h` du prototype : une icône bleue, un titre en famille
 * display, et ce que la carte porte à droite.
 *
 * Le titre est rendu en `<h2>` : une carte est une section d'un écran dont le `<h1>` est
 * celui de `PageHeader`, et c'est le niveau que les onglets de l'éditeur emploient déjà.
 * Descendre à `<h3>` sauterait un cran et casserait le plan du document — ce que la
 * navigation par titres d'un lecteur d'écran est précisément faite pour parcourir.
 */
export function CardHeader({ icon, title, divider = true, children, className }: CardHeaderProps) {
  return (
    <div className={cn(s.header, !divider && s.headerFlush, className)}>
      {icon !== undefined && (
        <span className={s.headerIcon} aria-hidden>
          {icon}
        </span>
      )}
      <h2 className={s.title}>{title}</h2>
      {children !== undefined && <div className={s.headerActions}>{children}</div>}
    </div>
  )
}

/** Le contenu rembourré d'une carte — le bloc que le prototype écrit en `padding:18px 20px`. */
export function CardBody({ className, ...props }: ComponentPropsWithRef<'div'>) {
  return <div className={cn(s.body, className)} {...props} />
}
